import { format, formatDateTime } from '@rental/i18n'
import { buttonVariants, cn } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { UserPlus } from 'lucide-react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { PageHeader } from '../components/PageHeader'
import { ListRow, ROW_LINK } from '../components/Results'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { useLocale } from '../i18n'
import { type PasswordState, type Person, passwordState } from '../people/people'
import { loadPeople, PEOPLE_KEY } from '../people/queries'
import { problemOf } from '../problems'

/**
 * S9 (ADM-1, Q-51): Persone, for the holders of users.manage (the Owner).
 * Anyone else who types its address reads the refusal under its title, and
 * nothing is asked of the server for it.
 */
export function PeopleScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'users.manage')} title={m.people.title}>
      <PeopleList />
    </Guard>
  )
}

/**
 * Everyone who uses the app, the active first, each with their role, the
 * state of their access and their last sign-in. A few people, never pages
 * of them: no search.
 */
function PeopleList() {
  const { messages: m } = useLocale()
  const people = useQuery({ queryKey: PEOPLE_KEY, queryFn: loadPeople })
  const now = Date.now()
  return (
    <>
      <PageHeader
        title={m.people.title}
        intro={m.people.intro}
        actions={
          <Link to="/people/new" className={buttonVariants({ variant: 'staff' })}>
            <UserPlus aria-hidden="true" className="size-4" />
            {m.people.add}
          </Link>
        }
      />
      <LoadState data={people.data} problem={problemOf(people.error)}>
        {(data) => {
          const active = data.items.filter((person) => person.disabledAt === null)
          const disabled = data.items.filter((person) => person.disabledAt !== null)
          return (
            <>
              <Section title={m.people.activeTitle}>
                <ul className="flex flex-col">
                  {active.map((person) => (
                    <PersonRow key={person.id} person={person} now={now} />
                  ))}
                </ul>
              </Section>
              {disabled.length > 0 ? (
                <Section title={m.people.disabledTitle}>
                  <ul className="flex flex-col">
                    {disabled.map((person) => (
                      <PersonRow key={person.id} person={person} now={now} />
                    ))}
                  </ul>
                </Section>
              ) : null}
            </>
          )
        }}
      </LoadState>
    </>
  )
}

/**
 * What a person's access is now, in words on its chips: shared by the list and
 * the person's page. A deactivated account says only that; a password still to
 * choose, or past its time, says so, never by its colour alone.
 */
export function PersonChips({ person, password }: { person: Person; password: PasswordState }) {
  const { messages: m } = useLocale()
  return (
    <>
      {person.self ? <StatusChip tone="info">{m.people.you}</StatusChip> : null}
      {person.disabledAt !== null ? (
        <StatusChip tone="neutral">{m.people.disabled}</StatusChip>
      ) : password === 'toChoose' ? (
        <StatusChip tone="warning">{m.people.passwordToChoose}</StatusChip>
      ) : password === 'expired' ? (
        <StatusChip tone="danger">{m.people.passwordExpired}</StatusChip>
      ) : null}
    </>
  )
}

function PersonRow({ person, now }: { person: Person; now: number }) {
  const { locale, messages: m } = useLocale()
  const password = passwordState(person, now)
  const role = (m.roles as Readonly<Record<string, string>>)[person.role] ?? person.role
  return (
    <ListRow
      title={
        <Link
          to="/people/$userId"
          params={{ userId: person.id }}
          className={cn('font-bold', ROW_LINK)}
        >
          {person.displayName}
        </Link>
      }
      end={<PersonChips person={person} password={password} />}
    >
      <span>{role}</span>
      <span className="wrap-anywhere">{person.email}</span>
      <span>
        {person.disabledAt !== null
          ? format(m.people.disabledOn, { date: formatDateTime(locale, person.disabledAt) })
          : person.lastUnlockAt
            ? format(m.people.lastUnlock, { date: formatDateTime(locale, person.lastUnlockAt) })
            : m.people.neverUnlocked}
      </span>
    </ListRow>
  )
}
